import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatFullDate } from "@/lib/format";
import { useLicenseLabels } from "@/hooks/useLicenseLabels";
import type { License } from "@/pages/licenses/hooks/useMyLicenses";

interface RenewalRequestModalProps {
  open: boolean;
  license: License | null;
  busy: boolean;
  onConfirm: (note: string) => void;
  onCancel: () => void;
}

export default function RenewalRequestModal({ open, license, busy, onConfirm, onCancel }: RenewalRequestModalProps) {
  const { t } = useTranslation();
  const { edition } = useLicenseLabels();
  const [note, setNote] = useState("");

  useEffect(() => {
    if (open) setNote("");
  }, [open]);

  if (!open || !license) return null;

  const expired = license.expires_at ? new Date(license.expires_at).getTime() < Date.now() : false;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-8">
      <button
        type="button"
        aria-label={t("licenses.renewalTitle")}
        onClick={() => {
          if (!busy) onCancel();
        }}
        className="absolute inset-0 cursor-pointer bg-foreground-950/50"
      />

      <div
        role="dialog"
        aria-modal="true"
        className="lh-fade-up relative z-10 flex max-h-full w-full max-w-lg flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50"
      >
        <div className="flex items-start gap-3 border-b border-background-200 px-5 py-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
            <i className="ri-refresh-line text-xl" />
          </span>
          <div className="flex-1">
            <h2 className="font-heading text-base font-semibold text-foreground-950">{t("licenses.renewalTitle")}</h2>
            <p className="mt-1 text-xs leading-relaxed text-foreground-600">{t("licenses.renewalDesc")}</p>
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            aria-label={t("licenses.cancel")}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="border-b border-background-200 bg-background-100 px-5 py-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="font-heading text-sm font-semibold text-foreground-950">{license.product_name}</p>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-foreground-500">
                <span className="rounded-md bg-background-50 px-2 py-0.5 text-foreground-700">
                  {edition(license.edition)}
                </span>
                <span className="font-mono">LH-••••-{license.license_key.slice(-4)}</span>
                <span>{t("licenses.seatUnit", { count: license.seat_count })}</span>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[11px] text-foreground-500">{t("licenses.renewalCurrentExpiry")}</p>
              <p className={`mt-0.5 text-sm font-medium ${expired ? "text-accent-700" : "text-foreground-950"}`}>
                {license.expires_at ? formatFullDate(license.expires_at) : t("licenses.permanent")}
                {expired && t("licenses.renewalExpiredSuffix")}
              </p>
            </div>
          </div>
        </div>

        <div className="px-5 py-4">
          <label className="flex flex-col gap-1.5 text-xs text-foreground-600">
            {t("licenses.renewalNoteLabel")}
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value.slice(0, 200))}
              rows={3}
              placeholder={t("licenses.renewalNotePlaceholder")}
              className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
            />
            <span className="self-end text-[11px] text-foreground-400">{note.length} / 200</span>
          </label>
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-background-200 bg-background-50 px-5 py-4">
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {t("licenses.cancel")}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onConfirm(note)}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-send-plane-line" />
            </span>
            {busy ? t("licenses.renewalSubmitting") : t("licenses.renewalSubmit")}
          </button>
        </div>
      </div>
    </div>
  );
}